import React from 'react';
import { Pencil, MoreVertical, Power, ArrowRightLeft, Users, Receipt, ShieldCheck, Globe2, UserSearch } from 'lucide-react';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '../ui/dropdown-menu';
import { PcBadge, PcIconButton } from './PricingUi';
import { methodLabel, appliesTo, fmtDate, money } from '../../utils/clientPricesApi';

const CAT = { TITLE_SEARCH: 'Title Search', TAX_MLS: 'Tax & MLS' };

const productIcon = (r) => {
  const n = (r.product_name || '').toLowerCase();
  if (n.includes('mls')) return { Icon: Users, bg: 'linear-gradient(135deg, #818cf8, #6366f1)' };
  if (r.product_category === 'TAX_MLS' || n.includes('tax')) return { Icon: Receipt, bg: 'linear-gradient(135deg, #34d399, #059669)' };
  if (n.includes('full')) return { Icon: ShieldCheck, bg: 'linear-gradient(135deg, #fbbf24, #d97706)' };
  if (r.search_method === 'online') return { Icon: Globe2, bg: 'linear-gradient(135deg, #38bdf8, #2563eb)' };
  return { Icon: UserSearch, bg: 'linear-gradient(135deg, #6366f1, #4338ca)' };
};

export const ClientPriceRow = ({ r, cfg, onEdit, onConvert, onDeactivate }) => {
  const { Icon, bg } = productIcon(r);
  const live = r.status !== 'inactive';
  const canConvert = cfg?.is_admin && r.is_legacy && live;
  const canManage = cfg?.can_manage && live;
  return (
    <tr title={`${r.scope_label} → ${money(r.price)}`} data-testid={`csp-row-${r.id}`}>
      <td>
        <div className="pc-product">
          <span className="pc-product-icon" style={{ background: bg, color: '#fff' }}><Icon /></span>
          <div className="min-w-0">
            <div className="pc-product-name">{r.product_name}</div>
            <div className="pc-sub">{CAT[r.product_category]}{r.is_legacy && <PcBadge tone="legacy" data-testid={`csp-legacy-pill-${r.id}`}>Legacy</PcBadge>}</div>
          </div>
        </div>
      </td>
      <td data-testid={`csp-applies-${r.id}`}>{appliesTo(r)}</td>
      <td className="pc-dim" data-testid={`csp-method-${r.id}`}>{methodLabel(r.search_method, r.product_category)}</td>
      <td className="pc-dim" data-testid={`csp-depth-${r.id}`}>{r.full_search_depth_years ? `${r.full_search_depth_years} Yr` : <span className="pc-dash">—</span>}</td>
      <td className="pc-right pc-price" data-testid={`csp-price-${r.id}`}>{money(r.price)}</td>
      <td className="pc-num pc-dim whitespace-nowrap" style={{ fontSize: 12.5 }}>{fmtDate(r.effective_from)} <span className="pc-dash">→</span> {r.effective_to ? fmtDate(r.effective_to) : 'ongoing'}</td>
      <td><PcBadge tone={r.status} data-testid={`csp-status-${r.id}`}>{r.status}</PcBadge></td>
      <td className="pc-right">
        <div className="pc-row-actions">
          {canManage && <PcIconButton icon={Pencil} title="Edit price / dates" onClick={() => onEdit(r)} data-testid={`csp-edit-${r.id}`} />}
          {(canManage || canConvert) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild><button type="button" className="pc-icon-btn" title="More actions" aria-label="More actions" data-testid={`csp-more-${r.id}`}><MoreVertical className="w-4 h-4" /></button></DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="pc-menu">
                {canConvert && <DropdownMenuItem className="pc-success" onSelect={() => onConvert(r)} data-testid={`csp-convert-${r.id}`}><ArrowRightLeft className="w-4 h-4" />Convert to Client Price</DropdownMenuItem>}
                {canManage && <DropdownMenuItem className="pc-danger" onSelect={() => onDeactivate(r)} data-testid={`csp-deactivate-${r.id}`}><Power className="w-4 h-4" />Deactivate</DropdownMenuItem>}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </td>
    </tr>
  );
};
